import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { AlertCircle, CheckCircle2, Loader2, Eye, EyeOff } from "lucide-react";
import { RoundLogo } from "../../components/shared/RoundLogo";
import { BrandLogo } from "../../components/shared/BrandLogo";

export default function CustomerSetPassword() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#1A3626";
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-primary" data-testid="customer-setpw-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }
  // Must be a signed-in customer to reach this gate.
  if (!user || user.role !== "customer") {
    return <Navigate to="/customer/login" replace />;
  }
  // Already set a permanent password — nothing to do here.
  if (!user.must_change_password) {
    return <Navigate to="/customer" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("Your new password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The two passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/customer/set-password", {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      // Refresh the cached user so must_change_password clears, then enter the app.
      await refreshUser();
      navigate("/customer", { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save your password. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex min-h-[100dvh] items-center justify-center bg-primary px-4"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="customer-set-password-page"
    >
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center justify-center gap-3">
          <RoundLogo className="h-20 w-20" />
          <BrandLogo className="h-14" />
        </div>
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <h1 className="font-heading text-lg font-semibold text-text-main">Create Your New Password</h1>
            <p className="mt-1 text-sm text-text-muted">
              For your security, please create a new password to continue. It must be at least 6 characters.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNew ? "text" : "password"}
                    data-testid="setpw-new-password-input"
                    autoFocus
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
                    aria-label={showNew ? "Hide password" : "Show password"}
                    data-testid="setpw-toggle-new-visibility"
                  >
                    {showNew ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? "text" : "password"}
                    data-testid="setpw-confirm-password-input"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    data-testid="setpw-toggle-confirm-visibility"
                  >
                    {showConfirm ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {newPassword && confirmPassword && newPassword === confirmPassword && newPassword.length >= 6 && (
                <div className="flex items-center gap-2 text-sm text-primary" data-testid="setpw-match-ok">
                  <CheckCircle2 size={16} strokeWidth={1.5} /> Passwords match
                </div>
              )}

              {error && (
                <div
                  className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger"
                  data-testid="setpw-error-message"
                >
                  <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting} data-testid="setpw-save-button">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                Save Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
